import type { UseFormSetError } from 'react-hook-form';
import { toast } from 'react-toastify/unstyled';

import { useRequestNewTraceMutation } from '@features/investigations/api/files.api';
import type { NewInvestigationFormValues } from '@features/investigations/components/modals/add-new-investigation/investigation.schema';
import { mapFormValuesToPayload } from '@features/investigations/components/modals/add-new-investigation/investigation.utils';
import type { RTKQueryError } from '@shared/utils/api.types';

export const useAddNewTrace = () => {
  const [requestNewTraceMutation, { isLoading }] = useRequestNewTraceMutation();

  const submitNewTrace = async (
    formData: NewInvestigationFormValues,
    setError: UseFormSetError<NewInvestigationFormValues>,
  ) => {
    try {
      const payload = mapFormValuesToPayload(formData);
      await requestNewTraceMutation(payload).unwrap();
      toast.success('Investigation requested successfully');
    } catch (err) {
      console.error('Submission failed:', err);

      const errorObj = err as RTKQueryError;
      const backendErrors = errorObj?.data?.errors ?? {};

      if (backendErrors.dateTo ?? backendErrors.dateFrom) {
        setError('dateRange', {
          type: 'server',
          message: backendErrors.dateTo ?? backendErrors.dateFrom,
        });
      }

      if (backendErrors.traceName) {
        setError('traceName', {
          type: 'server',
          message: backendErrors.traceName,
        });
      }

      toast.error(errorObj?.data?.message ?? 'Error creating new investigation');
      throw err;
    }
  };

  return {
    submitNewTrace,
    isLoading,
  };
};
